import { useQuery } from '@tanstack/react-query'
import { useUser } from '~/features/auth/api/useUser'
import { JsonRpcError } from '~/types'
import { WithdrawVerdicts } from '../types'

/** The verdict and payout-block pill for ONE request, fetched when it is expanded.
 *
 *  The list comes back without them (`with_verdicts: false`) because the triage
 *  behind a verdict is the whole cost of the page: 13-30 seconds a page on
 *  production on 2026-09-28, for rows most reviewers never open. Now a row pays
 *  for its own triage only when somebody opens it.
 *
 *  Cached for as long as the page is open: collapsing and re-opening a row, or
 *  paging away and back, is served from here rather than triaged again. The
 *  collapsed row keeps showing the verdict once it has one, because a query
 *  that is no longer enabled still returns what it cached. A reload asks again,
 *  which is the way to see a fresh verdict.
 */
export const useWithdrawVerdict = (uuid: string, enabled: boolean) => {
    const { data: user } = useUser()
    return useQuery<WithdrawVerdicts, JsonRpcError>({
        queryKey: ['withdraw_verdict', uuid],
        queryFn: async () => {
            const res = await fetch('/rpc', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'bank.withdraw_request.verdicts',
                    params: { uuids: [uuid] },
                }),
            })
            const data = await res.json()
            if (data.error) {
                throw data
            } else {
                return data.result
            }
        },
        retry: false,
        staleTime: Infinity,
        cacheTime: Infinity,
        enabled: enabled && !!uuid && user.is_auth && (user.is_marketing || user.is_executive),
    })
}

/** Verdicts for every row on the page, in ONE call, as soon as the list lands.
 *
 *  The per-row version above exists because triage is the whole cost of this
 *  page -- 13-30 seconds a page on production on 2026-09-28 -- and paying it
 *  for rows nobody opens was wasteful. But the result was a queue where a row
 *  showed no verdict, no score and no history until it was opened, which is
 *  the one thing the queue is for: knowing which rows need you WITHOUT
 *  opening them.
 *
 *  So the work still happens, but once per page rather than once per row, and
 *  after the list has already rendered. The rows appear immediately and the
 *  pills fill in behind them. Nothing blocks on it, and a failure costs the
 *  pills and not the queue.
 */
export const useWithdrawVerdictsFor = (uuids: string[], enabled: boolean) => {
    const { data: user } = useUser()
    const key = [...uuids].sort().join(',')
    return useQuery<WithdrawVerdicts, JsonRpcError>({
        queryKey: ['withdraw_verdicts', key],
        queryFn: async () => {
            const res = await fetch('/rpc', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    jsonrpc: '2.0',
                    id: 1,
                    method: 'bank.withdraw_request.verdicts',
                    params: { uuids },
                }),
            })
            const data = await res.json()
            if (data.error) throw data
            return data.result
        },
        retry: false,
        staleTime: Infinity,
        cacheTime: Infinity,
        enabled:
            enabled && uuids.length > 0 && user.is_auth && (user.is_marketing || user.is_executive),
    })
}
